import {Route} from '@angular/router';
import {UserRole} from '@emma-labs/shared/typescript';
import {AuthGuard} from './auth.guard';
import {DashboardGuard} from './dashboard.guard';
import {ArchivedProposalPageComponent} from './ui/archived-proposal-page/archived-proposal-page.component';
import {ArchivedProposalPageGuard} from './ui/archived-proposal-page/archived-proposal-page.guard';
import {authPageGuard} from './ui/auth/auth-page.guard';
import {AuthPageComponent} from './ui/auth/auth-page/auth-page.component';
import {ForgotPasswordPageComponent} from './ui/auth/forgot-password-page/forgot-password-page.component';
import {MultifactorAuthPageComponent} from './ui/auth/multifactor-auth-page/multifactor-auth-page.component';
import {PasswordResetPageComponent} from './ui/auth/password-reset-page/password-reset-page.component';
import {passwordResetPageGuard} from './ui/auth/password-reset-page/password-reset-page.guard';
import {ClientSearchPageComponent} from './ui/client-search-page/client-search-page.component';
import {DocumentationPageComponent} from './ui/documentation-page/documentation-page.component';
import {ProfilePageComponent} from './ui/profile-page/profile-page.component';
import {ProposalPageComponent} from './ui/proposal-page/proposal-page.component';
import {ProposalsPageComponent} from './ui/proposals-page/proposals-page.component';
import {QuickQuotePageComponent} from './ui/quick-quote/quick-quote-page.component';
import {QuoteComparatorComponent} from './ui/quote-comparator/quote-comparator.component';
import {SaleReportPageComponent} from './ui/sale-report-page/sale-report-page.component';
import {SaleReportSearchPageComponent} from './ui/sale-report-search-page/sale-report-search-page.component';
import {UnderwritingMibPageComponent} from './ui/underwriting-mib-page/underwriting-mib-page.component';
import {UnderwritingProfilePageComponent} from './ui/underwriting-profile-page/underwriting-profile-page.component';
import {UnderwritingProfilesPageComponent} from './ui/underwriting-profiles-page/underwriting-profiles-page.component';
import {UserRoleGuard} from './user-role.guard';

export const ROUTES: Route[] = [
  {
    path: '',
    redirectTo: '/auth',
    pathMatch: 'full',
  },
  {
    path: 'auth',
    title: 'Emma | Authentication',
    component: AuthPageComponent,
    canActivate: [authPageGuard],
  },
  {
    path: 'auth/forgot-password',
    title: 'Emma | Forgot Password',
    component: ForgotPasswordPageComponent,
    canActivate: [authPageGuard],
  },
  {
    path: 'auth/reset-password',
    title: 'Emma | Reset Password',
    component: PasswordResetPageComponent,
    canActivate: [authPageGuard, passwordResetPageGuard],
  },
  {
    path: 'auth/mfa',
    title: 'Emma | Multifactor Authentication',
    component: MultifactorAuthPageComponent,
    canActivate: [authPageGuard],
  },
  {
    path: 'dashboard',
    title: 'Emma | Dashboard',
    canActivate: [AuthGuard, DashboardGuard],
    canActivateChild: [AuthGuard],
    children: [
      {
        path: 'underwriting/:underwritingProfileId/mib',
        title: 'Emma | Proposal',
        component: UnderwritingMibPageComponent,
      },
      {
        path: 'underwriting',
        redirectTo: '/dashboard/underwriting/search',
        pathMatch: 'full',
      },
      {
        path: 'underwriting',
        title: 'Emma | Underwriting',
        canActivate: [UserRoleGuard],
        canActivateChild: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.UNDERWRITER, UserRole.CASE_MANAGER],
        },
        children: [
          {
            path: 'search',
            title: 'Emma | Search',
            component: UnderwritingProfilesPageComponent,
          },
          {
            path: ':underwritingProfileId',
            title: 'Emma | Proposal',
            component: UnderwritingProfilePageComponent,
          },
        ],
      },
      {
        path: 'proposals',
        title: 'Emma | Proposals',
        component: ProposalsPageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'profile',
        title: 'Emma | Profile',
        component: ProfilePageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'documentation',
        title: 'Emma | Documentation',
        component: DocumentationPageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'client-search',
        title: 'Emma | Client Search',
        component: ClientSearchPageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'sale-report-search',
        title: 'Emma | Sale Report Search',
        component: SaleReportSearchPageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'sale-report/:saleReportId',
        title: 'Emma | Sale Report',
        component: SaleReportPageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'proposal',
        title: 'Emma | Proposal',
        component: ProposalPageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'proposal/:proposalId',
        title: 'Emma | Proposal',
        component: ProposalPageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'archived-proposal/:archivedProposalId',
        title: 'Emma | Archived Proposal',
        component: ArchivedProposalPageComponent,
        canActivate: [UserRoleGuard, ArchivedProposalPageGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'quick-quote',
        title: 'Emma | Quick Quote',
        component: QuickQuotePageComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
      {
        path: 'quote-comparator',
        title: 'Emma | Quote Comparator',
        component: QuoteComparatorComponent,
        canActivate: [UserRoleGuard],
        data: {
          permittedUserRoles: [UserRole.ADVISOR, UserRole.SUPER_ADVISOR],
        },
      },
    ],
  },
  {path: '**', redirectTo: '/dashboard'},
];
